import channelsData from '../data/channels.json';
import titlesData from '../data/titles.json';
import usersData from '../data/users.json';
import interactionsData from '../data/interactions.json';
import castCrewData from '../data/cast_crew.json';
import { Channel, Title, UserProfile, Interaction, CastCredit } from '../data/types';

export const channels = channelsData as Channel[];
export const titles = titlesData as Title[];
export const users = usersData as UserProfile[];
export const interactions = interactionsData as Interaction[];
export const castCrew = castCrewData as CastCredit[];

const titleById = new Map(titles.map((t) => [t.title_id, t]));
const channelById = new Map(channels.map((c) => [c.channel_id, c]));

export function getChannel(channelId: string): Channel | undefined {
  return channelById.get(channelId);
}

export function getTitle(titleId: string): Title | undefined {
  return titleById.get(titleId);
}

function interactionsForUser(userId: string): Interaction[] {
  return interactions.filter((i) => i.user_id === userId);
}

/** Most recent interaction per title, sorted newest first. */
function latestPerTitle(ints: Interaction[]): Interaction[] {
  const seen = new Map<string, Interaction>();
  for (const i of ints) {
    const prev = seen.get(i.title_id);
    if (!prev || i.event_date > prev.event_date) seen.set(i.title_id, i);
  }
  return Array.from(seen.values()).sort((a, b) => (a.event_date < b.event_date ? 1 : -1));
}

export interface Row {
  id: string;
  title: string;
  items: RowItem[];
}

export interface RowItem {
  title: Title;
  channel?: Channel;
  progressPct?: number;
  reason?: string;
  bars?: [string, number][]; // fixed-order (label, contribution) pairs from the just_watch model, sums to score
  // Set when this pick came from a non-hybrid fallback path (cold-start popularity, or
  // rewatch once the demo's 30-title catalog is exhausted) — `bars` is all zero in this
  // case since there's no real attribution to show, so the UI should skip the chart.
  note?: string | null;
}

const ROW_LENGTH = 8;
const CONTINUE_WATCHING_LENGTH = 12;
const MORE_LIKE_THIS_LENGTH = 10;

/** Same-genre titles ranked by popularity — the "More Like This" row shown on a title's
 * detail page (recs/../pages/TitleDetailPage.tsx) and reused by the voice assistant's
 * "next movie" command to jump to the top pick without re-deriving the list. */
export function moreLikeThis(item: RowItem): Row {
  const similar = titles
    .filter((t) => t.genre_primary === item.title.genre_primary && t.title_id !== item.title.title_id)
    .sort((a, b) => b.popularity_score - a.popularity_score)
    .slice(0, MORE_LIKE_THIS_LENGTH)
    .map((title) => ({ title, channel: getChannel(title.channel_id) }));
  return { id: 'more-like-this', title: 'More Like This', items: similar };
}

function getContinueWatching(userId: string): RowItem[] {
  const ints = latestPerTitle(interactionsForUser(userId)).filter(
    (i) => !i.completed && i.watch_pct > 0.05 && i.watch_pct < 0.95
  );
  return ints
    .slice(0, CONTINUE_WATCHING_LENGTH)
    .map((i): RowItem | null => {
      const title = titleById.get(i.title_id);
      if (!title) return null;
      return { title, channel: channelById.get(title.channel_id), progressPct: i.watch_pct * 100 };
    })
    .filter((x): x is RowItem => x !== null);
}

function getLiveNow(user: UserProfile): RowItem[] {
  const live = titles.filter((t) => t.content_type === 'Live TV');
  const inLanguage = live.filter((t) => t.language === user.preferred_language);
  const rest = live.filter((t) => t.language !== user.preferred_language);
  const ordered = [...inLanguage, ...rest].sort((a, b) => {
    const aMatch = a.language === user.preferred_language ? 1 : 0;
    const bMatch = b.language === user.preferred_language ? 1 : 0;
    if (aMatch !== bMatch) return bMatch - aMatch;
    return b.popularity_score - a.popularity_score;
  });
  return ordered
    .slice(0, ROW_LENGTH)
    .map((title) => ({ title, channel: channelById.get(title.channel_id) }));
}

/** Fills a row up to `n` items from a ranked list of candidate groups, in
 * priority order, deduplicated by title_id — so a row backfills with the next
 * most-relevant group (e.g. already-watched) rather than coming up short when
 * the highest-priority group alone can't fill it. This demo's catalog is only
 * 30 titles, so a heavy watcher can otherwise see a near-empty or empty row. */
function fillRow(groups: Title[][], n: number): Title[] {
  const seen = new Set<string>();
  const out: Title[] = [];
  for (const group of groups) {
    for (const title of group) {
      if (out.length === n) return out;
      if (seen.has(title.title_id)) continue;
      seen.add(title.title_id);
      out.push(title);
    }
  }
  return out;
}

function getTrendingInLanguage(user: UserProfile, exclude: Set<string>): RowItem[] {
  const byPopularity = (a: Title, b: Title) => b.popularity_score - a.popularity_score;
  const inLanguage = titles.filter((t) => t.language === user.preferred_language).sort(byPopularity);

  // Backfill with already-watched titles in the SAME language only — never with
  // other languages, which would break the "Trending in [language]" promise.
  // Malay/Tamil-preferring personas have only 5 titles in-language total, so this
  // row can legitimately max out below ROW_LENGTH for them, same as Live Now's
  // real ceiling of 5 available channels.
  const filled = fillRow(
    [inLanguage.filter((t) => !exclude.has(t.title_id)), inLanguage.filter((t) => exclude.has(t.title_id))],
    ROW_LENGTH
  );
  return filled.map((title) => ({ title, channel: channelById.get(title.channel_id) }));
}

function getNewOnMeWatch(exclude: Set<string>): RowItem[] {
  const byRecency = (a: Title, b: Title) => (a.catalog_added_date < b.catalog_added_date ? 1 : -1);
  const sorted = [...titles].sort(byRecency);

  const filled = fillRow(
    [sorted.filter((t) => !exclude.has(t.title_id)), sorted.filter((t) => exclude.has(t.title_id))],
    ROW_LENGTH
  );
  return filled.map((title) => ({ title, channel: channelById.get(title.channel_id) }));
}

export function getRowsForUser(user: UserProfile): Row[] {
  const rows: Row[] = [];

  const continueWatching = getContinueWatching(user.user_id);
  if (continueWatching.length > 0) {
    rows.push({ id: 'continue-watching', title: 'Continue Watching', items: continueWatching });
  }

  const liveNow = getLiveNow(user);
  if (liveNow.length > 0) {
    rows.push({ id: 'live-now', title: 'Live Now', items: liveNow });
  }

  const watchedIds = new Set(interactionsForUser(user.user_id).map((i) => i.title_id));
  const trending = getTrendingInLanguage(user, watchedIds);
  if (trending.length > 0) {
    rows.push({ id: 'trending', title: `Trending in ${user.preferred_language}`, items: trending });
  }

  const fresh = getNewOnMeWatch(watchedIds);
  if (fresh.length > 0) {
    rows.push({ id: 'new', title: 'New on Watch', items: fresh });
  }

  return rows;
}
